import { useMemo } from 'react';
import { useAnalyticsData } from './useAnalyticsData';
import { useLearnBackendData } from './useLearnBackendData';
import { useCoreBackendData } from './useCoreBackendData';
import { useReviewCloudData } from './useReviewCloudData';
import { useReportBuilderWidgetData } from './useReportBuilderWidgetData';
import type { QueryFilter } from '../types/analytics';

// ─── Data source discriminant ────────────────────────────────────────────────

type DataSource =
  | 'report_builder'
  | 'core_backend'
  | 'review_cloud'
  | 'learn_backend'
  | 'analytics';

/**
 * Resolves which data source should handle a given widget configuration.
 * The order of checks matters — more specific signals take priority.
 */
export function resolveDataSource(
  templateKey?: string,
  metricKey?: string,
  queryConfig?: any,
  chartType?: string,
): DataSource {
  // Report builder — explicit data_source flag or report_builder_id present
  if (
    queryConfig?.data_source === 'report_builder' ||
    queryConfig?.report_builder_id ||
    queryConfig?.reportBuilderId
  ) {
    return 'report_builder';
  }

  // Core backend — profile card / user card widgets
  if (
    templateKey === 'mp_user_card' ||
    chartType === 'profile_card' ||
    metricKey?.startsWith('core_api:') ||
    queryConfig?.data_source === 'core_backend'
  ) {
    return 'core_backend';
  }

  // Review cloud — pending tasks / action widgets
  if (
    templateKey === 'mp_actions' ||
    chartType === 'pending_tasks_card' ||
    metricKey?.startsWith('review_api:') ||
    queryConfig?.data_source === 'review_cloud'
  ) {
    return 'review_cloud';
  }

  // Learn backend — only the mp_learning_plan widget goes directly to learn-backend
  if (templateKey === 'mp_learning_plan' || chartType === 'learning_plan_card') {
    return 'learn_backend';
  }

  // Default: analytics backend handles everything else (including all learn_* metric keys)
  return 'analytics';
}

// ─── Params ──────────────────────────────────────────────────────────────────

export interface UseWidgetDataSourceParams {
  templateKey?: string;
  metricKey?: string;
  chartType?: string;
  queryConfig?: any;
  visualizationConfig?: any;
  filters?: QueryFilter[];
  ownerUserId?: number | string;
  scoping?: string;
  externalData?: { data: any; loading: boolean; error: string | null };
  disabled?: boolean;
}

// ─── Return type ─────────────────────────────────────────────────────────────

export interface UseWidgetDataSourceResult {
  data: any;
  loading: boolean;
  error: string | null;
  metadata: any;
  /** Which source was resolved — useful for conditional rendering logic */
  dataSource: DataSource;
  /** Report-builder columns (only populated when dataSource === 'report_builder') */
  rbColumns: import('../types/reportBuilder').ColumnMeta[];
  /** Raw analytics result (needed for column/meta access) */
  analyticsResult: ReturnType<typeof useAnalyticsData>;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * useWidgetDataSource
 *
 * Orchestrator hook that replaces the five unconditional hook calls in
 * WidgetWrapper. It resolves which single data source owns the widget and
 * then disables the four irrelevant hooks by passing `undefined` / `false`
 * for their key params so they short-circuit internally without violating
 * the Rules of Hooks.
 */
export function useWidgetDataSource({
  templateKey,
  metricKey,
  chartType,
  queryConfig,
  filters,
  ownerUserId,
  scoping,
  externalData,
  disabled = false,
}: UseWidgetDataSourceParams): UseWidgetDataSourceResult {
  // ── Resolve data source ───────────────────────────────────────────────────
  const dataSource = useMemo(
    () => resolveDataSource(templateKey, metricKey, queryConfig, chartType),
    [templateKey, metricKey, queryConfig, chartType],
  );

  const isRB = !disabled && dataSource === 'report_builder';
  const isCore = !disabled && dataSource === 'core_backend';
  const isRC = !disabled && dataSource === 'review_cloud';
  const isLearn = !disabled && dataSource === 'learn_backend';
  const isAnalytics = !disabled && dataSource === 'analytics';

  // ── Report Builder ────────────────────────────────────────────────────────
  const reportBuilderId = isRB
    ? (queryConfig?.report_builder_id as string | undefined)
    : undefined;

  const {
    data: rbData,
    loading: rbLoading,
    error: rbError,
    columns: rbColumns,
  } = useReportBuilderWidgetData({
    reportBuilderId,
    scopingType: scoping,
    enabled: isRB && !!reportBuilderId,
  });

  // ── Core Backend ──────────────────────────────────────────────────────────
  // useCoreBackendData has no `enabled` prop — it checks isCoreWidget internally.
  // Pass undefined templateKey / metricKey to disable.
  const {
    data: coreData,
    loading: coreLoading,
    error: coreError,
  } = useCoreBackendData({
    templateKey: isCore ? templateKey : undefined,
    metricKey: isCore ? metricKey : undefined,
    queryConfig: isCore ? queryConfig : undefined,
    chartType: isCore ? chartType : undefined,
  });

  // ── Review Cloud ──────────────────────────────────────────────────────────
  // useReviewCloudData has no `enabled` prop — it checks isReviewCloudWidget internally.
  const {
    data: reviewCloudData,
    loading: reviewCloudLoading,
    error: reviewCloudError,
  } = useReviewCloudData({
    templateKey: isRC ? templateKey : undefined,
    metricKey: isRC ? metricKey : undefined,
    queryConfig: isRC ? queryConfig : undefined,
    chartType: isRC ? chartType : undefined,
    filters: isRC ? filters : undefined,
  });

  // ── Learn Backend ─────────────────────────────────────────────────────────
  const {
    data: learnData,
    loading: learnLoading,
    error: learnError,
  } = useLearnBackendData({
    templateKey: isLearn ? templateKey : undefined,
    metricKey: isLearn ? metricKey : undefined,
    queryConfig: isLearn ? queryConfig : undefined,
    chartType: isLearn ? chartType : undefined,
    enabled: isLearn,
  });

  // ── Analytics ─────────────────────────────────────────────────────────────
  // When externalData is provided, skip internal fetch and use external source.
  const hasExternalData = !!externalData;
  const analyticsResult = useAnalyticsData({
    templateKey: isAnalytics && !hasExternalData ? templateKey : undefined,
    metricKey: isAnalytics && !hasExternalData ? metricKey : undefined,
    queryConfig: isAnalytics && !hasExternalData ? queryConfig : undefined,
    filters: isAnalytics && !hasExternalData ? filters : undefined,
    ownerUserId,
    scoping,
    enabled: isAnalytics && !hasExternalData,
  });

  // ── Merge result ──────────────────────────────────────────────────────────
  let data: any = null;
  let loading = false;
  let error: string | null = null;
  let metadata: any = null;

  switch (dataSource) {
    case 'report_builder':
      data = rbData;
      loading = rbLoading;
      error = rbError;
      break;

    case 'core_backend':
      data = coreData;
      loading = coreLoading;
      error = coreError;
      break;

    case 'review_cloud':
      data = reviewCloudData;
      loading = reviewCloudLoading;
      error = reviewCloudError;
      break;

    case 'learn_backend':
      data = learnData;
      loading = learnLoading;
      error = learnError;
      break;

    case 'analytics':
    default:
      data = externalData?.data ?? analyticsResult.data;
      loading = externalData?.loading ?? analyticsResult.loading;
      error = externalData?.error ?? analyticsResult.error;
      // Merge schema columns into metadata so WidgetWrapper can access
      // analyticsMetadata.columns for table headers and multi_stat labels
      metadata = {
        ...(analyticsResult as any).metadata,
        columns: analyticsResult.columns,
      };
      break;
  }

  return {
    data,
    loading,
    error,
    metadata,
    dataSource,
    rbColumns,
    analyticsResult,
  };
}
